import { faCheckSquare, faSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { openModal } from '../../actions/modal.action';
import modalMessages from '../../messages/modal.messages';
import Types from '../../types';
import Spinner from '../Spinner';
import EditThresholdModal from '../EditThresholdModal';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import jwtDecode from 'jwt-decode';

import {
  createDraftSurvey,
  publishSurvey,
  unpublishSurvey,
  updateSurveyCustomNSize,
} from '../../actions/survey.action';
import types from '../../types';

import { selectedSurvey } from '../../actions/wizard.action';
import { ENABLE_SURVEY_WIZARD } from '../../config/appConfig';

const SurveyActions = ({
  id,
  isPublished,
  isDraft,
  spinner,
  isCustomNSize,
  minRespondents,
  hasChildren,
  parentId,
}) => {
  const dispatch = useDispatch();
  const [showEditThreshold, setShowEditThreshold] = useState(false);
  const jwt = useSelector((state) => state.session.jwt);
  const currentUser = jwtDecode(jwt);
  const [localCustomNSize, setLocalCustomNSize] = useState(isCustomNSize);

  const publishSurveyHandler = () => {
    openModal({
      className: 'modal-alert-width',
      button1Text: 'No',
      button1Action: 'closeModal',
      button2Text: 'Yes',
      button2Action: () => {
        dispatch(publishSurvey(id, jwt));
        dispatch({ type: Types.reducerTypes.HIDE_MODAL });
      },
      body: 'Publishing a survey will allow all users who are part of your Bento organization to view the survey. Do you want to publish this survey?',
    })(dispatch);
  };

  const unpublishSurveyHandler = () => {
    dispatch(unpublishSurvey(id, jwt));
  };

  const convertDraftIntoSurveyHandler = () => {
    dispatch(selectedSurvey(id));
    dispatch(openModal(modalMessages('draftIntoMainSurvey')));
  };

  const createDraftSurveyHandler = () => {
    dispatch(createDraftSurvey(id, jwt));
  };

  const deleteDraftSurveyHandler = () => {
    dispatch(selectedSurvey(id, isDraft, parentId));
    dispatch(openModal(modalMessages('deleteDraftSurvey')));
  };

  const updateCustomNSize = () => {
    openModal({
      className: 'modal-alert-width',
      button1Text: 'Cancel',
      button1Action: 'closeModal',
      button2Text: 'Yes',
      button2Action: () => {
        setLocalCustomNSize(!localCustomNSize);
        dispatch(updateSurveyCustomNSize(id, !localCustomNSize, jwt));
        dispatch({ type: types.reducerTypes.HIDE_MODAL });
      },
      imageSrc: '/icons/location.png',
      body: localCustomNSize ?
        'Do you want to display the N size for each question in this survey as a range instead of a single number?' :
        'Do you want to display the N size for each question in this survey as a single number instead of a range? ',
    })(dispatch);
  };

  const handleToggleEditThreshold = () => setShowEditThreshold(!showEditThreshold);

  return (
    <>
      <div className="Card-actions">
        <ul className="Card-actions-list">
          <li>
            <Link className="Card-link" to={`/surveys/${id}`}>
              <span className="Card-action-icon">
                <img src="/view-icon.svg" alt="View Survey" />
              </span>
              <span className="Card-action-name">View Survey</span>
            </Link>
          </li>
          {!isPublished && ENABLE_SURVEY_WIZARD && (
            <li>
              <Link className="Card-link" to={`/admin/survey/edit/${id}`}>
                <span className="Card-action-icon">
                  <img src="/edit-icon.svg" alt="Edit Icon" />
                </span>
                <span className="Card-action-name">Edit</span>
              </Link>
            </li>
          )}
          <li>
            <OverlayTrigger
              placement="top"
              trigger={isPublished ? 'hover' : []}
              overlay={
                <Tooltip>
                  Unpublish the survey to change your N-size settings
                </Tooltip>
              }
              delay={100}
            >
              <button
                type="button"
                className="Card-link"
                onClick={handleToggleEditThreshold}
                disabled={spinner === Types.spinnerTypes.publishSurvey || isPublished}
              >
                <span className="Card-action-icon">
                  <img src="/edit-icon.svg" alt="Edit Icon" />
                </span>
                <span className="Card-action-name">Custom threshold for N-size</span>
              </button>
            </OverlayTrigger>
          </li>
          <li>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  {!isPublished && <span>When this box is checked, users will see the N size for each question displayed as a range to protect the confidentiality of survey respondents.</span>}
                  {isPublished && <span>Unpublish the survey to change your N-size settings</span>}
                </Tooltip>
              }
              delay={100}
            >
              <button
                type="button"
                className="Card-link"
                onClick={updateCustomNSize}
                disabled={spinner === Types.spinnerTypes.publishSurvey || isPublished}
              >
                <span className="Card-action-icon">
                  <FontAwesomeIcon size="xs" icon={!localCustomNSize ? faCheckSquare : faSquare} />
                </span>
                <span className="Card-action-name">Display N-Size as a range</span>
              </button>
            </OverlayTrigger>
          </li>
          {isPublished && !hasChildren && (
            <li>
              <button
                type="button"
                className="Card-link"
                onClick={unpublishSurveyHandler}
                disabled={spinner === Types.spinnerTypes.publishSurvey}
              >
                <span className="Card-action-icon">
                  {spinner === Types.spinnerTypes.publishSurvey && <Spinner />}
                  {!(spinner === Types.spinnerTypes.publishSurvey) && (
                    <img src="/unpublish-icon.svg" alt="Unpublish Survey" />
                  )}
                </span>
                <span className="Card-action-name">Unpublish</span>
              </button>
            </li>
          )}
          {!isPublished && !isDraft && (
            <li>
              <button
                type="button"
                className="Card-link"
                onClick={publishSurveyHandler}
                disabled={spinner === Types.spinnerTypes.publishSurvey}
              >
                <span className="Card-action-icon">
                  {spinner === Types.spinnerTypes.publishSurvey && <Spinner />}
                  {!(spinner === Types.spinnerTypes.publishSurvey) && (
                    <img src="/publish-icon.svg" alt="Publish Survey" />
                  )}
                </span>
                <span className="Card-action-name">Publish</span>
              </button>
            </li>
          )}
          {isPublished && currentUser.role !== 'ORGANIZATION_ADMIN' && (
            <li>
              <button
                className="Card-link"
                type="button"
                onClick={createDraftSurveyHandler}
                disabled={spinner === Types.spinnerTypes.createDraftSurvey}
              >
                <span className="Card-action-icon">
                  {spinner === Types.spinnerTypes.createDraftSurvey && <Spinner />}

                  {!(spinner === Types.spinnerTypes.createDraftSurvey) && (
                    <img src="/create-draft-icon.svg" alt="Create Survey" />
                  )}
                </span>
                <span className="Card-action-name">Create draft survey</span>
              </button>
            </li>
          )}
          {!isPublished && isDraft && (
            <li>
              <button
                type="button"
                className="Card-link"
                onClick={convertDraftIntoSurveyHandler}
                disabled={spinner === Types.spinnerTypes.convertDraftIntoSurvey}
              >
                <span className="Card-action-icon">
                  {spinner === Types.spinnerTypes.convertDraftIntoSurvey && <Spinner />}
                  {!(spinner === Types.spinnerTypes.convertDraftIntoSurvey) && (
                    <img src="/convert-icon.svg" alt="Convert to main survey" />
                  )}
                </span>
                <span className="Card-action-name">Convert to main survey</span>
              </button>
            </li>
          )}
          {(!isPublished || isDraft) && (
            <li>
              <button
                type="button"
                className="Card-link"
                onClick={deleteDraftSurveyHandler}
                disabled={spinner === Types.spinnerTypes.deleteDraftSurvey}
              >
                <span className="Card-action-icon">
                  {spinner === Types.spinnerTypes.deleteDraftSurvey && <Spinner />}

                  {!(spinner === Types.spinnerTypes.deleteDraftSurvey) && (
                    <img src="/delete-icon.svg" alt="Delete Survey" />
                  )}
                </span>
                <span className="Card-action-name">Delete</span>
              </button>
            </li>
          )}
          {!isPublished && isDraft && (
            <li>
              <Link className="Card-link" to={`/admin/survey/replace/${id}`}>
                <span className="Card-action-icon">
                  <img src="/replace-icon.svg" alt="Replace data set" />
                </span>
                <span className="Card-action-name">Replace data set</span>
              </Link>
            </li>
          )}
        </ul>
        <EditThresholdModal
          showEditThreshold={showEditThreshold}
          handleToggleEditThreshold={handleToggleEditThreshold}
          minRespondents={minRespondents}
          id={id}
        />
      </div>
    </>
  );
};

SurveyActions.propTypes = {
  isPublished: PropTypes.bool.isRequired,
  id: PropTypes.number.isRequired,
  isDraft: PropTypes.bool.isRequired,
  isCustomNSize: PropTypes.bool.isRequired,
  hasChildren: PropTypes.bool.isRequired,
  parentId: PropTypes.number.isRequired,
};

export default SurveyActions;
